// Thin client for the Vaartix API. The session lives in an HttpOnly cookie set by /auth/verify,
// so requests only need `credentials: 'include'` and, for users in several teams, the team header.

// Development goes through Vite's /api proxy (see vite.config.ts). VITE_API_URL overrides both.
const BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  (import.meta.env.DEV ? '/api' : 'https://api.vaartix.com');
const TEAM_KEY = 'vx-team';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    message: string,
  ) {
    super(message);
  }
}

export function currentTeamId(): string | null {
  try {
    return localStorage.getItem(TEAM_KEY);
  } catch {
    return null;
  }
}

export function setCurrentTeamId(id: string) {
  try {
    localStorage.setItem(TEAM_KEY, id);
  } catch {
    // Private mode: the first team is used.
  }
}

export async function api<T>(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  const team = currentTeamId();
  if (team) headers['x-vaartix-team'] = team;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Could not reach Vaartix. Check your connection.');
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data as { error?: { type?: string; message?: string } } | null)?.error;
    throw new ApiError(
      res.status,
      err?.type ?? 'api_error',
      err?.message ?? `Request failed (${res.status}).`,
    );
  }
  return data as T;
}

/* ---------- Types (mirror the API reference) ---------- */

export type Role = 'owner' | 'admin' | 'member';

export interface Team {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: 'active' | 'paused' | 'suspended';
  role: Role;
  /** Whether the sign-up questions have been answered. */
  onboarded: boolean;
}

export interface Me {
  user: { id: string; email: string };
  teams: Team[];
  /** Vaartix operators see the admin pages. */
  isAdmin?: boolean;
  /** Dashboard 2FA: `required` for admins, who can't use the admin panel without it. */
  twoFactor?: { enabled: boolean; required: boolean };
}

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt: string | null;
  required: boolean;
  recoveryCodesLeft: number;
}

export type EmailStatus =
  | 'queued'
  | 'scheduled'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'delivery_delayed'
  | 'bounced'
  | 'complained'
  | 'suppressed'
  | 'failed'
  | 'canceled';

export interface Tag {
  name: string;
  value: string;
}

export interface EmailSummary {
  id: string;
  from: string;
  to: string[];
  cc: string[];
  bcc: string[];
  replyTo: string[];
  subject: string;
  status: EmailStatus;
  tags: Tag[];
  attachments: { filename: string; contentType: string; size: number }[];
  scheduledAt: string | null;
  sentAt: string | null;
  lastEventAt: string | null;
  error: string | null;
  createdAt: string;
}

export interface EmailEvent {
  type: string;
  recipient: string | null;
  data: Record<string, unknown>;
  occurredAt: string;
}

export interface Email extends EmailSummary {
  html: string | null;
  text: string | null;
  headers: Record<string, string>;
  events: EmailEvent[];
}

export interface DnsRecord {
  type: 'CNAME' | 'MX' | 'TXT';
  /** Full record name, such as `vx-bounces.test.acme.in`. */
  name: string;
  /** The name relative to the registrable domain (`vx-bounces.test`): what goes in the Name field. */
  host?: string;
  value: string;
  priority?: number;
  status?: 'pending' | 'verified' | 'failed';
}

export interface Domain {
  id: string;
  name: string;
  status: 'pending' | 'verified' | 'failed' | 'temporary_failure';
  region: string;
  records: DnsRecord[];
  verifiedAt: string | null;
  lastCheckedAt: string | null;
  createdAt: string;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scope: 'send' | 'full';
  /** Whether the key is limited to `domainIds` (which can be empty after domains are deleted). */
  domainRestricted: boolean;
  domainIds: string[];
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export type WebhookEventType =
  | 'email.sent'
  | 'email.delivered'
  | 'email.delivery_delayed'
  | 'email.bounced'
  | 'email.complained'
  | 'email.rejected'
  | 'email.rendering_failure'
  | 'email.suppressed';

export interface Webhook {
  id: string;
  url: string;
  events: WebhookEventType[];
  status: 'enabled' | 'disabled';
  createdAt: string;
}

export interface WebhookDelivery {
  id: string;
  type: WebhookEventType;
  eventId: string | null;
  status: 'pending' | 'succeeded' | 'failed';
  attempts: number;
  responseStatus: number | null;
  responseBody: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  createdAt: string;
  payload: { type: string; createdAt: string; data: Record<string, unknown> };
}

/** One entry in Settings → Audit log. */
export interface AuditEntry {
  id: number;
  action: string;
  actor:
    | { type: 'member'; email: string | null; name: string | null }
    | { type: 'api_key' }
    | { type: 'vaartix' };
  targetId: string | null;
  targetName: string | null;
  details: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

/** A dashboard login of the current user (Settings → Active sessions). */
export interface DashboardSession {
  id: string;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

export interface TeamDetails {
  id: string;
  name: string;
  plan: string;
  status: string;
  dailyLimit: number;
  createdAt: string;
  members?: { userId: string; email: string; name: string | null; role: Role; joinedAt: string }[];
}

export interface BillingDetails {
  legalName: string;
  gstin: string | null;
  address: string;
  stateCode: string;
  email: string;
}

export interface PlanInfo {
  domains: number;
  teamMembers: number;
  monthlyEmails: number | null;
  retentionDays: number;
}

export interface Billing {
  plan: string;
  limits: PlanInfo;
  usage: { month: string; sent: number };
  subscription: {
    id: string;
    plan: string;
    status: 'created' | 'authenticated' | 'active' | 'pending';
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  details: BillingDetails | null;
  invoices: { id: string; number: string; plan: string; totalPaise: number; issuedAt: string }[];
  plans: (PlanInfo & { id: string; name: string; pricePaise: number; grossPaise: number })[];
  free: PlanInfo;
  enabled: boolean;
  testMode: boolean;
  states: { code: string; name: string }[];
}

export interface Checkout {
  keyId: string;
  subscriptionId: string;
  plan: string;
  planName: string;
  amountPaise: number;
  prefill: { name: string; email: string };
}

export interface Invoice {
  id: string;
  number: string;
  plan: string;
  planName: string;
  paymentId: string;
  periodStart: string | null;
  periodEnd: string | null;
  taxablePaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  totalPaise: number;
  seller: { legalName: string; gstin: string; address: string; stateCode: string; sac: string };
  sellerState: string;
  buyer: BillingDetails;
  buyerState: string;
  issuedAt: string;
}

export interface ApiRequestSummary {
  id: string;
  method: string;
  path: string;
  statusCode: number;
  durationMs: number;
  apiKeyId: string | null;
  apiKeyName: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface ApiRequestDetail extends ApiRequestSummary {
  query: string | null;
  ip: string | null;
  requestBody: unknown;
  responseBody: unknown;
}
